"use client";
import { useTranslations } from "next-intl";
import Image from "next/image";

type TimelineItem = {
  year: string;
  title: string;
  description: string;
};

type StatItem = {
  value: string;
  label: string;
};

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Terminal%201%20y%202%20fachada%20d%C3%ADa%20y%20noche/321A8777.jpg";

const TIMELINE_IMAGES = [
  "https://galeria.tocumenaero.com/gallery/api/Fotos%20vintage-Antiguas/images.jpg",
  "https://galeria.tocumenaero.com/gallery/api/Fotos%20vintage-Antiguas/Foto_3.jpg",
  "https://galeria.tocumenaero.com/gallery/api/Fotos%20vintage-Antiguas/Foto_4.jpg",
  "https://galeria.tocumenaero.com/gallery/api/Generales/45cd54f9-32e3-4c75-84bb-22bcd5edb43a.jpg",
  "https://galeria.tocumenaero.com/gallery/api/Fotos%20Construcci%C3%B3n%20T2/Terminal_existente_1.jpg",
  "https://galeria.tocumenaero.com/gallery/api/Generales/321A0362.jpg",
];

export default function HistoriaPage() {
  const t = useTranslations("historiaPage");
  const timeline = t.raw("timeline") as TimelineItem[];
  const stats = t.raw("stats") as StatItem[];

  return (
    <main className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("title")}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px bg-white" />
              {t("eyebrow")}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
              {t("title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("subtitle")}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-[80%] mx-auto py-24">
        {/* Timeline */}
        <div className="relative">
          {/* Línea vertical */}
          <div className="absolute top-0 bottom-0 w-px bg-gray-200 left-4 md:left-1/2 md:-translate-x-1/2" />

          <ol className="space-y-20 md:space-y-32">
            {timeline.map((item, index) => {
              const isLeft = index % 2 === 0;
              const image = TIMELINE_IMAGES[index % TIMELINE_IMAGES.length];
              return (
                <li
                  key={item.year}
                  className="relative md:grid md:grid-cols-[1fr_auto_1fr] md:gap-8 md:items-center"
                >
                  {/* MOBILE: year chip + content stacked */}
                  <div className="md:hidden flex items-center gap-4 mb-6 pl-0">
                    <span className="relative z-10 inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white border border-[#102542] text-base font-bold text-[#102542] tracking-tight tabular-nums shadow-sm">
                      {item.year}
                    </span>
                  </div>
                  <div className="md:hidden pl-12 space-y-4">
                    <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-gray-100 border border-gray-200">
                      <Image
                        src={image}
                        alt={item.title}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 tracking-tight">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  {/* DESKTOP: image | year | card (alternating) */}
                  <div
                    className={`hidden md:block ${isLeft ? "md:order-1" : "md:order-3"}`}
                  >
                    <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 border border-gray-200">
                      <Image
                        src={image}
                        alt={item.title}
                        fill
                        className="object-cover"
                      />
                    </div>
                  </div>

                  {/* Year en el centro del timeline */}
                  <div className="hidden md:flex md:order-2 items-center justify-center">
                    <span className="relative z-10 inline-flex items-center justify-center px-5 py-2 rounded-full bg-white border-2 border-[#102542] text-xl lg:text-2xl font-bold text-[#102542] tracking-tight tabular-nums shadow-sm whitespace-nowrap">
                      {item.year}
                    </span>
                  </div>

                  <div
                    className={`hidden md:block ${isLeft ? "md:order-3" : "md:order-1"}`}
                  >
                    <div className="bg-white border border-gray-200 rounded-2xl p-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 tracking-tight">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Stats */}
        <div className="mt-24 pt-16 border-t border-gray-200">
          <div className="max-w-3xl mb-14">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#102542] font-semibold mb-4">
              <span className="w-8 h-px bg-[#102542]" />
              {t("statsEyebrow")}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              {t("statsTitle")}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="bg-white p-8 flex flex-col justify-between min-h-[160px] transition-colors hover:bg-gray-50"
              >
                <span className="text-3xl md:text-4xl font-bold text-[#102542] tracking-tight tabular-nums">
                  {stat.value}
                </span>
                <span className="text-sm text-gray-500 leading-relaxed mt-4">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
